import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Mail, Phone, Building2, Users, Trash2, CheckCircle, Clock, XCircle, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const STATUS_CONFIG = {
  new: { label: 'Nuevo', bg: 'bg-blue-50 text-blue-700', icon: Clock },
  contacted: { label: 'Contactado', bg: 'bg-amber-50 text-amber-700', icon: ArrowUpRight },
  converted: { label: 'Convertido', bg: 'bg-green-50 text-green-700', icon: CheckCircle },
  dismissed: { label: 'Descartado', bg: 'bg-gray-100 text-gray-600', icon: XCircle },
};

export default function ContactRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { fetchRequests(); }, []);

  const fetchRequests = async () => {
    try {
      const res = await API.get('/contact-requests');
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await API.put(`/contact-requests/${id}/status?status=${status}`);
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch (err) {
      console.error(err);
    }
  };

  const deleteRequest = async (id) => {
    try {
      await API.delete(`/contact-requests/${id}`);
      setRequests(prev => prev.filter(r => r.id !== id));
      setSelected(null);
    } catch (err) {
      console.error(err);
    }
  };

  const newCount = requests.filter(r => r.status === 'new').length;
  const demoCount = requests.filter(r => r.request_type === 'demo').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contact-requests-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">Solicitudes</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {requests.length} solicitudes totales — {newCount} nuevas — {demoCount} demos
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const count = requests.filter(r => r.status === key).length;
          return (
            <div key={key} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{cfg.label}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{count}</p>
            </div>
          );
        })}
      </div>

      {/* List */}
      <div className="space-y-3">
        {requests.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center text-gray-400">
            <Mail className="w-10 h-10 mx-auto mb-3 text-gray-200" />
            <p>No hay solicitudes todavia</p>
          </div>
        ) : (
          requests.map(req => {
            const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.new;
            const StatusIcon = cfg.icon;
            return (
              <div
                key={req.id}
                onClick={() => setSelected(req)}
                className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 hover:shadow-md hover:border-orange-200 transition-all cursor-pointer"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-gray-900">{req.name}</span>
                      <Badge className={`${cfg.bg} text-xs gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {cfg.label}
                      </Badge>
                      <Badge className={`text-xs ${req.request_type === 'demo' ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                        {req.request_type === 'demo' ? 'Demo' : 'Contacto'}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{req.email}</span>
                      {req.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{req.phone}</span>}
                      {req.company && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{req.company}</span>}
                      {req.sector && <span>{req.sector}</span>}
                      {req.employees && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{req.employees}</span>}
                    </div>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-1">{req.message}</p>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {new Date(req.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{selected?.name}</DialogTitle>
            <DialogDescription>
              {selected?.request_type === 'demo' ? 'Solicitud de demo' : 'Mensaje de contacto'} — {selected?.created_at && new Date(selected.created_at).toLocaleString('es-ES')}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-xs text-gray-500 block">Email</span><span className="font-medium">{selected.email}</span></div>
                <div><span className="text-xs text-gray-500 block">Telefono</span><span className="font-medium">{selected.phone || '—'}</span></div>
                <div><span className="text-xs text-gray-500 block">Empresa</span><span className="font-medium">{selected.company || '—'}</span></div>
                <div><span className="text-xs text-gray-500 block">Sector</span><span className="font-medium">{selected.sector || '—'}</span></div>
                <div><span className="text-xs text-gray-500 block">Empleados</span><span className="font-medium">{selected.employees || '—'}</span></div>
              </div>
              <div>
                <span className="text-xs text-gray-500 block mb-1">Mensaje</span>
                <p className="text-sm text-gray-900 bg-gray-50 rounded-lg p-3">{selected.message}</p>
              </div>
              {/* Status actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={selected.status === key ? 'default' : 'outline'}
                    className={selected.status === key ? 'bg-[#FF6B35] text-white' : ''}
                    onClick={() => updateStatus(selected.id, key)}
                  >
                    {cfg.label}
                  </Button>
                ))}
                <Button size="sm" variant="outline" className="text-red-500 hover:bg-red-50 ml-auto" onClick={() => deleteRequest(selected.id)}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />
                  Eliminar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
